import { PriceBreakdown, ProductDiscount } from '@monolith/legacy/types/product';
import { Amount } from '@core/types/amount';
import { DineroObject } from 'dinero.js';

const getRawDiscountsValues = function (originalPrice: DineroObject, ...discounts: ProductDiscount[]): Record<string, number> {
  const result: Record<string, number> = {};

  for (const discount of discounts) {
    if (discount?.percentage) {
      result[discount.origin] = (originalPrice.amount / 100) * discount.percentage;
    }
  }

  return result;
};

const getDiscountsValues = function (originalPrice: DineroObject, ...discounts: ProductDiscount[]): Record<string, Amount> {
  const result: Record<string, Amount> = {};

  for (const [origin, amount] of Object.entries(getRawDiscountsValues(originalPrice, ...discounts))) {
    result[origin] = {
      ...originalPrice,
      amount: Math.round(amount),
    };
  }

  return result;
};

const getPriceBreakdown = function (
  originalPrice: DineroObject,
  retailPrice: DineroObject,
  wholesalePrice: DineroObject,
  ...discounts: ProductDiscount[]
): PriceBreakdown {
  const rawDiscountValues = getRawDiscountsValues(originalPrice, ...discounts);
  const discountValues = getDiscountsValues(originalPrice, ...discounts);
  let basePrice = originalPrice.amount;

  if (!discountValues['brand'] && originalPrice.amount !== wholesalePrice.amount) {
    // in this case we have a black friday event and a product discount
    basePrice = wholesalePrice.amount;
  }

  // The discounts are summed unrounded and the total is rounded once, at the end. The
  // backend derives the discounted price the same way, so a discount landing on half a
  // cent resolves to the price the retailer is actually charged.
  const totalDiscount = Object.values(rawDiscountValues).reduce((total, amount) => total + amount, 0);
  const finalPrice = Math.round(basePrice - totalDiscount);

  const discountOrigins = Object.keys(discountValues);
  if (discountOrigins.length === 1) {
    // With a single discount, show the amount that reconciles exactly with the final
    // price rather than the independently-rounded one.
    const [origin] = discountOrigins;
    discountValues[origin] = {
      ...discountValues[origin],
      amount: basePrice - finalPrice,
    };
  }

  return {
    originalPrice,
    retailPrice,
    wholesalePrice,
    discounts: discountValues,
    finalPrice: {
      amount: finalPrice,
      currency: originalPrice.currency,
    },
  };
};

const getDiscount = function (percentage: number, origin: string): ProductDiscount {
  if (!percentage) {
    return null;
  }

  return {
    origin,
    percentage,
  };
};

export { getDiscountsValues, getPriceBreakdown, getDiscount };
